/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useEffect, useRef } from "react";

/**
 * Volume-button SOS trigger — placeholder hook. Capacitor has no first-party
 * volume-key listener, iOS blocks background volume capture, and Android
 * requires a custom native plugin that intercepts `KEYCODE_VOLUME_UP` in a
 * foreground service. Shipping that work is deferred to Session 5.
 *
 * Consumers can call this hook today; on every platform it will currently
 * report `supported: false` and never invoke `onTrigger`.
 */
export interface VolumeButtonTriggerOpts {
  onTrigger: () => void;
  /** Number of rapid presses that constitute an SOS trigger. Default 5. */
  pattern?: number;
  /** Window within which `pattern` presses must occur. Default 3000ms. */
  windowMs?: number;
  enabled?: boolean;
}

export function useVolumeButtonTrigger(opts: VolumeButtonTriggerOpts): {
  supported: boolean;
} {
  const warnedRef = useRef(false);

  useEffect(() => {
    if (!opts.enabled) return;
    if (warnedRef.current) return;
    warnedRef.current = true;
    // Keep this as an info-level log so it doesn't pollute Sentry.
    // TODO(session-5): wire a custom Capacitor plugin (Android KeyEvent
    // intercept; iOS is not feasible) to actually listen for volume-up
    // presses and count them over `windowMs`.
    console.info(
      "[useVolumeButtonTrigger] Not supported on this platform — stub only.",
    );
  }, [opts.enabled]);

  return { supported: false };
}
